import { Fragment, useEffect, useLayoutEffect, useRef, useState, ViewTransition } from "react";
import { Outlet, useHref, useLocation, useNavigate, useNavigationType } from "react-router";
import { onFromApp, postToApp, tellAppReady } from "../lib/inApp";
import { useSending } from "../sending/SendingContext";
import { SendingBanner } from "../sending/SendingBanner";
import { prefetchTalk } from "../talk/prefetch";
import { prefetchHome } from "../views/home/HomeView";
import { prefetchPeople, prefetchPerson } from "../views/people/list";
import { prefetchRooms } from "../views/rooms/RoomsView";
import { prefetchRoom } from "../views/rooms/useRoom";
import { FlashsuiteBar, useFlashsuiteScript } from "./FlashsuiteMenu";
import { LimitNotice } from "./LimitNotice";
import { useTrackHistory } from "./phone/history";
import { swipe, useEdgeSwipeBack, useInstallable, usePhoneViewport, usePullToRefresh } from "./phone/shell";
import { isTabRoot, TAB_ROOTS, TabBar, tabOf, tabRoot } from "./phone/TabBar";
import { UNREAD_CHANGED } from "./phone/unread";
import { useLayout } from "./season/layout";
import { TopBar } from "./TopBar";

/**
 * The operator shell: the top bar over a scrolling workspace, as in Flashkut and Flashadz. Each
 * route renders into the `.main` outlet. On a phone the bar gives way to the tab bar docked at the bottom,
 * shown on each tab's own screen; on an iPad the bar is the Season one (see TopBar).
 */
export function AppShell() {
  useTrackHistory();
  usePhoneViewport();
  useEdgeSwipeBack();
  useInstallable();
  useFlashsuiteScript();
  const main = useRef<HTMLElement>(null);
  useScrollMemory(main);
  useOpenEarly();
  const { pathname } = useLocation();
  const phone = useLayout() === "phone";
  const slide = useSlide(pathname, phone);
  const sending = useSending();
  // A pull to refresh draws the screen again from scratch, so it loads afresh.
  const [epoch, setEpoch] = useState(0);
  const pullRing = usePullToRefresh(() => {
    setEpoch((n) => n + 1);
    window.dispatchEvent(new Event(UNREAD_CHANGED));
    void sending.refresh();
  });
  // Shown again by the phone app: each screen reads its own data again (useShownAgain), the shell the parts
  // every screen shares.
  useInsideApp(() => {
    window.dispatchEvent(new Event(UNREAD_CHANGED));
    void sending.refresh();
  });

  return (
    <div className={phone && isTabRoot(pathname) ? "app tabbed" : "app"}>
      <TopBar />
      <ViewTransition update={slide} default="none">
        <main className="main" ref={main}>
          <FlashsuiteBar />
          <SendingBanner />
          <LimitNotice />
          <Fragment key={epoch}>
            <Outlet />
          </Fragment>
        </main>
      </ViewTransition>
      <TabBar />
      <div className="ph-pull" ref={pullRing} aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5" />
        </svg>
      </div>
    </div>
  );
}

/**
 * Inside the Flashsuite phone app (flashsuite-phone docs/APP-MODE.md sections 3 and 4): tells the app where
 * the screen is on every move, takes its tab-again back to the tab's own screen, and runs `onShown` when
 * the app shows the screen again. Outside the app nothing is posted and nothing is heard.
 */
function useInsideApp(onShown: () => void): void {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const path = useHref(pathname + search);
  useEffect(() => {
    postToApp({ v: 1, kind: "place", path, tab: tabOf(pathname), root: isTabRoot(pathname) });
  }, [path, pathname]);
  useEffect(tellAppReady, []);
  const shown = useRef(onShown);
  shown.current = onShown;
  const at = useRef(pathname);
  at.current = pathname;
  useEffect(
    () =>
      onFromApp((m) => {
        if (m.kind === "shown") {
          shown.current();
          return;
        }
        if (m.kind !== "tab-again") return;
        const to = tabRoot(m.tab);
        if (to && (at.current.replace(/\/+$/, "") || "/") !== to) navigate(to);
      }),
    [navigate],
  );
}

/** The places the bars lead to. Coming back to one finds it where it was left, as a phone's tabs do. */
const PLACES = new Set([...TAB_ROOTS, "/automations", "/settings"]);
const isPlace = (pathname: string) => PLACES.has(pathname.replace(/\/+$/, "") || "/");
/** How far a screen sits from the tabs: a tab is the bottom, More's Settings one above it. */
const depth = (pathname: string) => (isTabRoot(pathname) ? 0 : pathname.split("/").filter(Boolean).length);

/**
 * How a phone moves from one screen to the next: a screen opened from a list slides in over it, going
 * back slides it away, and moving between the tabs is instant. The class names are drawn in phone.css.
 */
export function slideBetween(from: string, to: string): "fw-forward" | "fw-back" | "none" {
  if (depth(to) > depth(from)) return "fw-forward";
  if (depth(to) < depth(from)) return "fw-back";
  return "none";
}

/** The slide for this move, animated by React's view transitions, which the router's own navigations start. */
function useSlide(pathname: string, phone: boolean): string {
  const before = useRef(pathname);
  useLayoutEffect(() => {
    before.current = pathname;
    swipe.back = false;
  }, [pathname]);
  useEffect(() => {
    // Why: the browser's own swipe back (Safari, Chrome on Android) has already moved the page.
    const pop = (e: PopStateEvent) => {
      if (e.hasUAVisualTransition) swipe.back = true;
    };
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, []);
  return phone && !swipe.back ? slideBetween(before.current, pathname) : "none";
}

const TABS_AFTER_MS = 1500;
/** How long a finger rests on a row before its page starts loading: a finger that scrolls the list moves sooner. */
export const HOLD_MS = 120;

/** What pressing on `target` would open, as the load that gets it ready; null when it opens nothing. */
function opening(target: EventTarget | null): (() => void) | null {
  const at = target instanceof Element ? target : null;
  const marked = at?.closest<HTMLElement>("[data-opens]")?.dataset.opens;
  const link = at?.closest<HTMLAnchorElement>("a[href]");
  const path = link ? new URL(link.href, window.location.href).pathname : "";
  const webinar = marked?.startsWith("webinar:") ? marked.slice(8) : /\/rooms\/([^/]+)$/.exec(path)?.[1];
  const person = marked?.startsWith("person:") ? marked.slice(7) : /\/people\/([^/]+)$/.exec(path)?.[1];
  if (webinar && webinar !== "new") return () => prefetchRoom(webinar);
  if (person) return () => prefetchPerson(person);
  return null;
}

/**
 * A webinar or a person starts loading as it is pressed, so it opens ready: at once under a
 * mouse, and under a finger once it rests, since a finger that scrolls the list opens nothing. A row says
 * what it opens with `data-opens`; a link says it by where it goes. The tabs' own screens load a moment after
 * the app opens, so the first tap on each opens ready too.
 */
function useOpenEarly(): void {
  useEffect(() => {
    let resting = 0;
    const down = (e: PointerEvent) => {
      const open = opening(e.target);
      if (!open) return;
      if (e.pointerType === "touch") resting = window.setTimeout(open, HOLD_MS);
      else open();
    };
    // Why: the browser cancels a touch the moment it turns into a scroll.
    const scrolled = () => window.clearTimeout(resting);
    document.addEventListener("pointerdown", down, { capture: true, passive: true });
    document.addEventListener("pointercancel", scrolled, { capture: true, passive: true });
    // Why after a wait: the screen the person opened loads first; the tabs follow while they read it.
    const tabs = window.setTimeout(() => {
      prefetchHome();
      prefetchRooms();
      prefetchPeople();
      prefetchTalk();
    }, TABS_AFTER_MS);
    return () => {
      document.removeEventListener("pointerdown", down, { capture: true });
      document.removeEventListener("pointercancel", scrolled, { capture: true });
      window.clearTimeout(resting);
      window.clearTimeout(tabs);
    };
  }, []);
}

/**
 * A new page opens at its top; going back returns to where that page was left.
 * Why: every page shares the one `.main` scroller, so without this a page
 * opened from halfway down a long list opened halfway down too.
 */
function useScrollMemory(main: React.RefObject<HTMLElement | null>): void {
  const { key, pathname, hash } = useLocation();
  const type = useNavigationType();
  const saved = useRef(new Map<string, number>());
  /** Each place's own scroll: a tab tapped again opens where it was left. */
  const byPlace = useRef(new Map<string, number>());
  const current = useRef(key);
  // A page that keeps its filters in the address changes the key, not the place.
  const place = useRef(pathname + hash);

  useEffect(() => {
    const el = main.current;
    if (!el) return;
    const remember = () => {
      saved.current.set(current.current, el.scrollTop);
      byPlace.current.set(place.current, el.scrollTop);
    };
    el.addEventListener("scroll", remember, { passive: true });
    return () => el.removeEventListener("scroll", remember);
  }, [main]);

  useLayoutEffect(() => {
    const prevKey = current.current;
    current.current = key;
    const samePlace = place.current === pathname + hash;
    place.current = pathname + hash;
    const el = main.current;
    if (!el) return;
    if (samePlace) {
      saved.current.set(key, saved.current.get(prevKey) ?? el.scrollTop);
      return;
    }
    const to =
      type === "POP" ? (saved.current.get(key) ?? 0) : isPlace(pathname) ? (byPlace.current.get(pathname + hash) ?? 0) : 0;
    el.scrollTop = to;
    if (to === 0) return;
    // The page may still be loading: try again once it has had time to grow.
    const later = window.setTimeout(() => {
      if (current.current === key) el.scrollTop = to;
    }, 350);
    return () => window.clearTimeout(later);
  }, [key, pathname, hash, type, main]);
}
